// Badges priorité/échéance éditables en popover, sans ouvrir la modale.
// Rendus dans TaskCard (liste et board). Le déclencheur est un <button>
// → le onClick du TaskCard l'ignore déjà (closest('button')), et le
// PopoverContent est portalisé hors de la carte.
import { useState } from 'react';
import type { Task } from '../../hooks/queries/useTasks';
import { useUpdateTask } from '../../hooks/queries/useTasks';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import {
  mergeDueDate,
  mergeDueTime,
  extractDueTime,
  extractDueDay,
  clearDue,
} from '../../lib/tasks/taskReschedule';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { PRIORITY_META, getPriorityMeta, getDueDate, getDueBadge } from './taskHelpers';

function isoLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function TaskPriorityBadge({ task }: { task: Task }) {
  const { priority: showPriority } = useEffectiveTaskFields();
  const [open, setOpen] = useState(false);
  const updateTask = useUpdateTask();
  const meta = getPriorityMeta(task.priority);
  const isDefault = task.priority === 3;

  function pick(priority: number) {
    if (priority !== task.priority) updateTask.mutate({ taskId: task.id, updates: { priority } });
    setOpen(false);
  }

  if (!showPriority) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Changer la priorité"
          className={`text-xs font-medium px-1.5 py-0.5 rounded border transition ${
            isDefault
              ? // Reste visible tant que le popover est ouvert : la souris quitte
                // la carte (fin du group-hover) et un anchor en display:none
                // ferait fuir le popover en haut a gauche (rect 0,0).
                `${open ? 'inline-flex' : 'hidden group-hover:inline-flex'} border-dashed border-cime-border text-cime-text-muted hover:border-cime-primary`
              : `${meta.className} hover:opacity-80`
          }`}
        >
          {meta.label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        data-cime-modal
        onClick={(e) => e.stopPropagation()}
        className="w-auto p-1 rounded-lg border-cime-border bg-white shadow-lg"
      >
        <div className="flex flex-col gap-0.5">
          {[1, 2, 3, 4].map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => pick(p)}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs font-medium text-left hover:bg-[#fbfaf7] ${
                p === task.priority ? 'bg-cime-primary/5' : ''
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${PRIORITY_META[p]!.dot}`} aria-hidden />
              {PRIORITY_META[p]!.label}
              {p === 3 && <span className="text-cime-text-muted font-normal">— normale</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TaskDueBadge({ task }: { task: Task }) {
  const [open, setOpen] = useState(false);
  const updateTask = useUpdateTask();
  const due = getDueDate(task);
  const badge = getDueBadge(task);

  function apply(updates: { due_date?: string | null; due_datetime?: string | null }) {
    updateTask.mutate({ taskId: task.id, updates });
    setOpen(false);
  }
  function pickDay(offsetDays: number) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    apply(mergeDueDate(task, isoLocal(d)));
  }

  const shortcuts: [string, number][] = [["Aujourd'hui", 0], ['Demain', 1], ['+1 semaine', 7]];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={badge?.title ?? "Changer l'échéance"}
          style={badge?.style}
          className={
            badge
              ? `text-xs tabular-nums whitespace-nowrap transition hover:opacity-80 ${
                  badge.bordered
                    ? `font-medium px-1.5 py-0.5 rounded border ${badge.className}`
                    : badge.className
                }`
              : // Meme garde que le badge priorite : anchor visible tant que le
                // popover est ouvert, sinon il fuit en haut a gauche.
                `${open ? 'inline-flex' : 'hidden group-hover:inline-flex'} text-xs px-1.5 py-0.5 rounded border border-dashed border-cime-border text-cime-text-muted hover:border-cime-primary`
          }
        >
          {badge?.label ?? '+ échéance'}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        data-cime-modal
        onClick={(e) => e.stopPropagation()}
        className="w-auto p-2 rounded-lg border-cime-border bg-white shadow-lg"
      >
        <div className="flex flex-col gap-1 min-w-[180px]">
          {shortcuts.map(([label, offset]) => (
            <button
              key={label}
              type="button"
              onClick={() => pickDay(offset)}
              className="px-2.5 py-1.5 rounded text-xs font-medium text-left text-cime-text-primary hover:bg-[#fbfaf7]"
            >
              {label}
            </button>
          ))}
          <input
            type="date"
            defaultValue={due ? due.slice(0, 10) : ''}
            onChange={(e) => {
              if (e.target.value) apply(mergeDueDate(task, e.target.value));
            }}
            className="px-2 py-1.5 rounded border border-cime-border text-xs text-cime-text-primary"
            aria-label="Choisir une date"
          />
          <input
            type="time"
            value={extractDueTime(task.due_datetime)}
            onChange={(e) => {
              const isoDay = extractDueDay(task) || isoLocal(new Date());
              apply(mergeDueTime(task, isoDay, e.target.value || null));
            }}
            className="px-2 py-1.5 rounded border border-cime-border text-xs text-cime-text-primary"
            aria-label="Choisir une heure"
          />
          {due && (
            <button
              type="button"
              onClick={() => apply(clearDue())}
              className="px-2.5 py-1.5 rounded text-xs font-medium text-left text-cime-urgent hover:bg-[#fbfaf7]"
            >
              Retirer l'échéance
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
